import { Link } from 'react-router-dom';
import { ExternalLink, RefreshCw, TrendingUp, Wallet } from 'lucide-react';

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { adminApi, type AdminMrrBreakdown, type AdminRecentPayment } from '@/services/adminApi';
import { useRefreshableFetch } from '@/hooks/admin/useRefreshableFetch';

/**
 * AdminFinancePage — cross-tenant finance overview.
 *
 * MRR breakdown is computed live from `companies.billing.planId` ×
 * `PLANS[planId].priceMonthly` (no Autumn round trip), cached 5 min on
 * the BE. Recent payments fan out to Autumn for active companies — the
 * `payment_log` collection in EM-05 will replace it.
 */
export default function AdminFinancePage() {
  const mrr = useRefreshableFetch<AdminMrrBreakdown>(() => adminApi.finance.mrrBreakdown(), []);
  const payments = useRefreshableFetch<{ items: AdminRecentPayment[]; count: number }>(
    () => adminApi.finance.recentPayments(),
    [],
  );

  const refreshAll = () => {
    mrr.refresh();
    payments.refresh();
  };

  return (
    <div className="space-y-6 max-w-6xl">
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Wallet className="h-5 w-5" /> Finance
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Cross-tenant MRR breakdown and recent payments. Both views refresh on demand.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="gap-1.5"
          onClick={refreshAll}
          disabled={mrr.isRefreshing || payments.isRefreshing}
        >
          <RefreshCw className={`h-3.5 w-3.5 ${(mrr.isRefreshing || payments.isRefreshing) ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiTile label="Total MRR" value={fmt(mrr.data?.totalMrr ?? 0)} hint="Sum of monthly plan prices for active companies" />
        <KpiTile label="Total ARR" value={fmt(mrr.data?.totalArr ?? 0)} hint="MRR × 12" />
        <KpiTile label="Plans on offer" value={String(mrr.data?.byPlan.length ?? 0)} hint="Distinct planIds with at least 1 active customer" />
        <KpiTile label="Recent payments" value={String(payments.data?.count ?? 0)} hint="Last 50 paid invoices across all customers" />
      </div>

      {/* MRR by plan + by segment */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="border-border/50">
          <CardContent className="p-0">
            <div className="px-5 py-3 border-b border-border/50">
              <h2 className="text-sm font-semibold flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
                MRR by plan
              </h2>
            </div>
            {mrr.loading ? (
              <p className="p-5 text-sm text-muted-foreground">Loading...</p>
            ) : !mrr.data || mrr.data.byPlan.length === 0 ? (
              <p className="p-5 text-sm text-muted-foreground">No active subscriptions yet.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border/40">
                    <th className="text-left py-2 px-4 font-medium text-muted-foreground">Plan</th>
                    <th className="text-right py-2 px-3 font-medium text-muted-foreground">Companies</th>
                    <th className="text-right py-2 px-3 font-medium text-muted-foreground">MRR</th>
                    <th className="text-right py-2 px-4 font-medium text-muted-foreground">ARR</th>
                  </tr>
                </thead>
                <tbody>
                  {mrr.data.byPlan.map((row) => (
                    <tr key={row.planId} className="border-b border-border/20 last:border-0">
                      <td className="py-2 px-4 font-mono text-xs">
                        <Link
                          to={`/admin/customers?plan=${row.planId}`}
                          className="hover:underline"
                        >
                          {row.planId}
                        </Link>
                      </td>
                      <td className="py-2 px-3 text-right tabular-nums">{row.count}</td>
                      <td className="py-2 px-3 text-right tabular-nums">{fmt(row.mrr)}</td>
                      <td className="py-2 px-4 text-right tabular-nums">{fmt(row.arr)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>

        <Card className="border-border/50">
          <CardContent className="p-0">
            <div className="px-5 py-3 border-b border-border/50">
              <h2 className="text-sm font-semibold">MRR by segment</h2>
            </div>
            {!mrr.data || mrr.data.bySegment.length === 0 ? (
              <p className="p-5 text-sm text-muted-foreground">No data.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border/40">
                    <th className="text-left py-2 px-4 font-medium text-muted-foreground">Segment</th>
                    <th className="text-right py-2 px-3 font-medium text-muted-foreground">Companies</th>
                    <th className="text-right py-2 px-3 font-medium text-muted-foreground">MRR</th>
                    <th className="text-right py-2 px-4 font-medium text-muted-foreground">ARR</th>
                  </tr>
                </thead>
                <tbody>
                  {mrr.data.bySegment.map((row) => (
                    <tr key={row.segment} className="border-b border-border/20 last:border-0">
                      <td className="py-2 px-4 capitalize">
                        <Link to={`/admin/customers?segment=${row.segment}`} className="hover:underline">
                          {row.segment}
                        </Link>
                      </td>
                      <td className="py-2 px-3 text-right tabular-nums">{row.count}</td>
                      <td className="py-2 px-3 text-right tabular-nums">{fmt(row.mrr)}</td>
                      <td className="py-2 px-4 text-right tabular-nums">{fmt(row.arr)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent payments */}
      <Card className="border-border/50">
        <CardContent className="p-0">
          <div className="px-5 py-3 border-b border-border/50">
            <h2 className="text-sm font-semibold">Recent payments</h2>
          </div>
          {payments.loading ? (
            <p className="p-5 text-sm text-muted-foreground">Loading...</p>
          ) : !payments.data || payments.data.items.length === 0 ? (
            <p className="p-5 text-sm text-muted-foreground">No paid invoices yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border/40">
                    <th className="text-left py-2 px-4 font-medium text-muted-foreground">When</th>
                    <th className="text-left py-2 px-3 font-medium text-muted-foreground">Company</th>
                    <th className="text-left py-2 px-3 font-medium text-muted-foreground">Invoice</th>
                    <th className="text-right py-2 px-3 font-medium text-muted-foreground">Amount</th>
                    <th className="text-right py-2 px-4 font-medium text-muted-foreground">PDF</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.data.items.map((p) => (
                    <tr key={p.invoiceId} className="border-b border-border/20 last:border-0">
                      <td className="py-2 px-4 text-xs text-muted-foreground">
                        {new Date(p.created).toLocaleString('en-GB')}
                      </td>
                      <td className="py-2 px-3">
                        <Link to={`/admin/customers/${p.companyId}`} className="hover:underline">
                          {p.companyName}
                        </Link>
                      </td>
                      <td className="py-2 px-3 text-xs font-mono">{p.invoiceId}</td>
                      <td className="py-2 px-3 text-right tabular-nums">
                        {fmtCurrency(p.amountPaid, p.currency)}
                      </td>
                      <td className="py-2 px-4 text-right">
                        {p.hostedInvoiceUrl ? (
                          <a
                            href={p.hostedInvoiceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:underline inline-flex items-center gap-1 text-xs"
                          >
                            View <ExternalLink className="h-3 w-3" />
                          </a>
                        ) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function KpiTile({ label, value, hint }: { label: string; value: string; hint: string }) {
  return (
    <Card className="border-border/50">
      <CardContent className="p-4">
        <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
        <p className="mt-1 text-xl font-semibold tabular-nums">{value}</p>
        <p className="text-[10px] text-muted-foreground mt-1 line-clamp-2">{hint}</p>
      </CardContent>
    </Card>
  );
}

function fmt(amount: number): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(amount);
}

function fmtCurrency(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency.toUpperCase(),
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${amount} ${currency.toUpperCase()}`;
  }
}
